import React from "react";
import { Box, Button, Flex } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";

export interface SlidingToggleOption {
	label: string;
	icon: string;
	activeBg: string;
	activeColour: string;
	onSelect: () => void;
}

interface Props {
	options: [SlidingToggleOption, SlidingToggleOption];
	selectedIndex: 0 | 1;
	/** Renders a smaller, compact variant used inside a rule row. */
	size?: "md" | "sm";
	isDisabled?: boolean;
}

const TRACK_PADDING = "3px";
const OPTION_GAP = "4px";
const TRANSITION = "0.2s ease";

/**
 * Two-option segmented toggle. The selected option's fill is a single indicator that slides
 * between the options and fades to the new option's colour.
 */
export function SlidingToggle(props: Props): JSX.Element {
	const { options, selectedIndex, size = "md", isDisabled = false } = props;
	const compact = size === "sm";
	const selected = options[selectedIndex];

	return (
		<Flex
			position="relative"
			bg={qoriaColours.brand.neutral[10]}
			border="1px solid"
			borderColor={qoriaColours.brand.neutral[100]}
			rounded="lg"
			p={TRACK_PADDING}
			gap={OPTION_GAP}
		>
			<Box
				aria-hidden
				position="absolute"
				top={TRACK_PADDING}
				bottom={TRACK_PADDING}
				left={TRACK_PADDING}
				width={`calc(50% - ${TRACK_PADDING} - ${OPTION_GAP} / 2)`}
				rounded="md"
				bg={selected.activeBg}
				opacity={isDisabled ? 0.4 : 1}
				transform={selectedIndex === 1 ? `translateX(calc(100% + ${OPTION_GAP}))` : "translateX(0)"}
				transition={`transform ${TRANSITION}, background-color ${TRANSITION}`}
				sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
			/>
			{options.map((option, index) => {
				const isSelected = index === selectedIndex;
				return (
					<Button
						key={option.label}
						position="relative"
						flex={1}
						variant="unstyled"
						display="flex"
						alignItems="center"
						justifyContent="center"
						gap={2}
						fontWeight={500}
						height={compact ? 8 : 10}
						px={compact ? 3 : 4}
						fontSize={compact ? "sm" : "md"}
						rounded="md"
						bg="transparent"
						color={isSelected ? option.activeColour : colours.text.regular}
						transition={`color ${TRANSITION}, background-color ${TRANSITION}`}
						_hover={isSelected || isDisabled ? undefined : { bg: qoriaColours.brand.neutral[50] }}
						sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
						isDisabled={isDisabled}
						leftIcon={<ChakraFAIcon icon={option.icon} variant="solid" boxSize={4} />}
						onClick={option.onSelect}
						aria-pressed={isSelected}
					>
						{option.label}
					</Button>
				);
			})}
		</Flex>
	);
}
